import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Box, Chip, Link as MuiLink, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import moment from 'moment'
import EmissionService from '../../../../shared/services/emission/emission.service'
import { IEmissionAuditCalculation, IEmissionAuditSource, IEmissionAuditTrail } from '../../../../shared/services/emission/emission.model'
import { provisionalFactorLabel, estimatedFactorLabel } from '../../../../shared/services/emission-calculation/emission-calculation.model'
import { ShallowFormDisplay } from '../../form-field-components/general/shallow-form-display'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'

const formatNumber = (value: number | null | undefined, maxDecimals = 2): string => {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: maxDecimals }).format(value)
}

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—'
  return moment(value).format('DD/MM/YYYY')
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  return moment(value).format('DD/MM/YYYY HH:mm')
}

const SOURCE_TYPE_LABELS: Record<IEmissionAuditSource['sourceType'], string> = {
  FILE_IMPORT: 'File Import',
  DOCUMENT_EXTRACTION: 'Bill Extraction',
  INTEGRATION: 'Integration',
  SYSTEM_GENERATED: 'System Generated',
  MANUAL_ENTRY: 'Manual Entry',
  API: 'API',
  METER_READING: 'Meter Reading',
}

const ACTION_LABELS: Record<string, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  DELETED: 'Deleted',
}

function SectionHeading({ title }: { title: string }) {
  return (
    <Typography variant='subtitle1' sx={{ mt: 3, mb: 1 }}>
      <b>{title}</b>
    </Typography>
  )
}

function DataSourceSection({ source }: { source: IEmissionAuditSource }) {
  const rows: { name: string; value: ReactNode }[] = []

  if (source.sourceType === 'FILE_IMPORT') {
    rows.push({
      name: 'File',
      value: source.filename ? (
        <MuiLink
          component={RouterLink}
          to='/settings/data-upload/import-history'
          sx={{ color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: '2px' }}
        >
          {source.filename}
        </MuiLink>
      ) : 'File no longer available',
    })
    rows.push({ name: 'Row Number', value: source.rowNumber != null ? String(source.rowNumber) : '—' })
    rows.push({ name: 'Uploaded By', value: source.uploadedBy || '—' })
    rows.push({ name: 'Upload Date', value: formatDateTime(source.uploadDate) })
    if (source.autoMatched != null) {
      rows.push({ name: 'Factor Matching', value: source.autoMatched ? 'Matched automatically' : 'Matched manually' })
    }
  } else if (source.sourceType === 'DOCUMENT_EXTRACTION') {
    // Mechanically an import, but the figures were read off a document rather than typed — the
    // distinction a verifier needs (FR13). The bill itself is attached to the emission, so it is
    // reachable as evidence rather than only named here.
    rows.push({ name: 'Bill', value: source.filename || 'Bill no longer available' })
    if (source.billType) {
      rows.push({ name: 'Bill Type', value: source.billType.charAt(0).toUpperCase() + source.billType.slice(1) })
    }
    rows.push({ name: 'Data Source', value: source.description || 'Extracted from an uploaded bill, then confirmed by a user' })
    rows.push({ name: 'Confirmed By', value: source.uploadedBy || '—' })
    rows.push({ name: 'Upload Date', value: formatDateTime(source.uploadDate) })
  } else if (source.sourceType === 'INTEGRATION') {
    rows.push({ name: 'Connection', value: source.connectionName || '—' })
  } else if (source.sourceType === 'SYSTEM_GENERATED') {
    rows.push({ name: 'Generated From', value: source.description || '—' })
  } else if (source.sourceType === 'API') {
    rows.push({ name: 'API Key', value: source.apiKeyName || '—' })
    rows.push({ name: 'Key Owner', value: source.enteredBy || '—' })
    rows.push({ name: 'Received At', value: formatDateTime(source.enteredAt) })
  } else {
    rows.push({ name: 'Entered By', value: source.enteredBy || '—' })
    rows.push({ name: 'Entered At', value: formatDateTime(source.enteredAt) })
  }

  return (
    <>
      <SectionHeading title='Data Source' />
      <ShallowFormDisplay small={true} isTopRow={true} name='Source' value={SOURCE_TYPE_LABELS[source.sourceType]} />
      {rows.map((row, index) => (
        <ShallowFormDisplay small={true} key={row.name} isBottomRow={index === rows.length - 1} name={row.name} value={row.value} />
      ))}
    </>
  )
}

// Which reporting total(s) this calculation contributes to. Scope 2 is split by
// basis (both location and market are reported); Scope 1/3 count toward both;
// MEASUREMENT rows are intermediate inputs, not emissions.
function reportingBasis(calculation: IEmissionAuditCalculation): string | null {
  if (calculation.type === 'MEASUREMENT') return null
  if (calculation.scope === '2') return calculation.flags.isMarketBased ? 'Market-based total' : 'Location-based total'
  if (calculation.scope === '1' || calculation.scope === '3') return 'Location & market totals'
  return null
}

function CalculationFlagChips({ calculation }: { calculation: IEmissionAuditCalculation }) {
  const { flags } = calculation
  const isScopeTwo = calculation.scope === '2'
  return (
    <Stack direction='row' flexWrap='wrap' gap={1}>
      <Chip size='small' label={calculation.type} />
      {calculation.scope && <Chip size='small' label={`Scope ${calculation.scope}`} variant='outlined' />}
      {flags.isWtt && <Chip size='small' label='Well-to-Tank' variant='outlined' />}
      {isScopeTwo && (
        <Chip
          size='small'
          variant='outlined'
          color='info'
          label={flags.isMarketBased ? 'Market-based' : 'Location-based'}
        />
      )}
      {flags.isFlag && <Chip size='small' label='FLAG' variant='outlined' color='warning' />}
      {flags.inflationAdjustmentApplied && <Chip size='small' label='Inflation-adjusted' variant='outlined' />}
      {flags.isProvisional && <Chip size='small' label={provisionalFactorLabel(flags.provisionalReason)} variant='outlined' />}
      {flags.isEstimated && <Chip size='small' label={estimatedFactorLabel(flags.estimatedReason)} variant='outlined' color='warning' />}
      {isScopeTwo && flags.isSelected && (
        <Chip size='small' variant='outlined' color='primary' label='Preferred method' />
      )}
      {calculation.type === 'MEASUREMENT' && (
        <Chip size='small' variant='outlined' label='Measurement input' sx={{ color: 'text.secondary' }} />
      )}
    </Stack>
  )
}

function CalculationCard({ calculation }: { calculation: IEmissionAuditCalculation }) {
  const input = calculation.unitsConsumed
  const unit = calculation.measurementUnit || ''
  const result = calculation.emissionAmount
  const resultUnit = calculation.greenHouseGasUnit || 'kgCO2e'
  const factorSource = calculation.factor.source
    ? `${calculation.factor.source}${calculation.factor.year ? ` (${calculation.factor.year})` : ''}`
    : 'Factor not recorded'

  return (
    <Paper variant='outlined' sx={{ p: 3 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            {calculation.category || calculation.group || 'Calculation'}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {formatDate(calculation.startDate)} – {formatDate(calculation.endDate)}
            {calculation.calendarYear ? ` · Calendar year ${calculation.calendarYear}` : ''}
          </Typography>
        </Box>
        <CalculationFlagChips calculation={calculation} />
      </Stack>
      <Box sx={{ bgcolor: 'lightGrey.light', borderRadius: '10px', px: 2, py: 1.5, mb: 1.5 }}>
        <Typography variant='body1' sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatNumber(input, 4)}{unit ? ` ${unit}` : ''} × {formatNumber(calculation.multiplier, 6)} = <b>{formatNumber(result, 2)} {resultUnit}</b>
        </Typography>
      </Box>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Emission factor: <b>{factorSource}</b>
        {calculation.factor.description ? `, ${calculation.factor.description}` : ''}
      </Typography>
      {reportingBasis(calculation) ? (
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
          Counts toward: <b>{reportingBasis(calculation)}</b>
        </Typography>
      ) : calculation.type === 'MEASUREMENT' ? (
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
          Measurement input: not counted as an emission in reporting totals.
        </Typography>
      ) : null}
      {calculation.inflation && calculation.inflation.applied && (
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
          Inflation adjustment: original spend {formatNumber(calculation.inflation.originalSpend, 2)} adjusted to {formatNumber(calculation.inflation.adjustedSpend, 2)}
          {calculation.inflation.indexName ? ` using ${calculation.inflation.indexName}` : ''}
          {calculation.inflation.ratio != null ? ` (ratio ${formatNumber(calculation.inflation.ratio, 4)})` : ''}
        </Typography>
      )}
      {/* FY-8: a skipped adjustment for missing CPI is a data-quality gap, not a clean no-op. */}
      {calculation.inflation && !calculation.inflation.applied && calculation.inflation.reason === 'CPI_MISSING' && (
        <Typography variant='body2' sx={{ color: 'warning.main', mt: 0.5 }}>
          Inflation adjustment not applied: CPI data is missing for this currency or year.
        </Typography>
      )}
      {/* A bridged adjustment is applied but approximate: the exact year has no CPI value yet. */}
      {calculation.inflation && calculation.inflation.applied && calculation.inflation.reason === 'BRIDGED' && (
        <Typography variant='body2' sx={{ color: 'warning.main', mt: 0.5 }}>
          Inflation adjustment approximated using the closest available CPI year; add the missing year value to make it exact.
        </Typography>
      )}
    </Paper>
  )
}

function ChangeValueCell({ change }: { change: IEmissionAuditTrail['changeHistory'][number] }) {
  const isUpdate = change.actionDescription === 'UPDATED'
  // Prefer the resolved friendly name; keep the raw id available beneath for audit reference.
  const oldDisplay = change.oldValueLabel || change.oldValue || '—'
  const newDisplay = change.newValueLabel || change.newValue || '—'
  const hasResolvedName = !!(change.oldValueLabel || change.newValueLabel)
  const rawOld = change.oldValue ?? '—'
  const rawNew = change.newValue ?? '—'

  return (
    <Stack spacing={0.25}>
      <Typography variant='body2' sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {isUpdate ? `${oldDisplay} → ${newDisplay}` : newDisplay}
      </Typography>
      {hasResolvedName && (
        <Typography variant='caption' sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
          {isUpdate ? `${rawOld} → ${rawNew}` : rawNew}
        </Typography>
      )}
    </Stack>
  )
}

function ChangeHistorySection({ auditTrail }: { auditTrail: IEmissionAuditTrail }) {
  return (
    <>
      <SectionHeading title='Change History' />
      <Paper variant='outlined' sx={{ p: 3 }}>
        {auditTrail.changeHistory.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            No changes recorded since creation.
          </Typography>
        ) : (
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, pl: 0, whiteSpace: 'nowrap' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Record</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditTrail.changeHistory.map((change) => (
                  <TableRow key={change.id}>
                    <TableCell sx={{ pl: 0, whiteSpace: 'nowrap' }}>{formatDateTime(change.createdAt)}</TableCell>
                    <TableCell>{change.user || '—'}</TableCell>
                    <TableCell>{ACTION_LABELS[change.actionDescription] || change.actionDescription}</TableCell>
                    <TableCell>{change.referenceModelName || '—'}</TableCell>
                    <TableCell>{change.propertyName || '—'}</TableCell>
                    <TableCell>
                      <ChangeValueCell change={change} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  )
}

export const EmissionAuditTrail: FunctionComponent<{ emissionId: string }> = ({ emissionId }) => {
  const emissionService = new EmissionService()
  const [auditTrail, setAuditTrail] = useState<IEmissionAuditTrail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    setErrorMessage('')
    emissionService.getAuditTrail(emissionId)
      .then((response) => {
        if (isActive) setAuditTrail(response)
      })
      .catch((err) => {
        console.error(err)
        if (isActive) setErrorMessage('Unable to load the audit trail. Please try again.')
      })
      .finally(() => {
        if (isActive) setIsLoading(false)
      })
    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionId])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
        <LoadingSpinner />
      </Box>
    )
  }

  if (errorMessage || !auditTrail) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{errorMessage || 'Unable to load the audit trail.'}</Typography>
      </Box>
    )
  }

  const hasScopeTwo = auditTrail.calculations.some((calculation) => calculation.scope === '2')
  // Emission-contributing calcs (everything except measurement inputs), ordered so the
  // primary calcs come first (scope asc, preferred method first), then upstream WTT.
  const contributing = auditTrail.calculations
    .filter((calculation) => calculation.type !== 'MEASUREMENT')
    .sort((a, b) => {
      if (!!a.flags.isWtt !== !!b.flags.isWtt) return a.flags.isWtt ? 1 : -1
      const sa = Number(a.scope || 99)
      const sb = Number(b.scope || 99)
      if (sa !== sb) return sa - sb
      if (!!a.flags.isSelected !== !!b.flags.isSelected) return a.flags.isSelected ? -1 : 1
      return 0
    })
  const measurements = auditTrail.calculations.filter((calculation) => calculation.type === 'MEASUREMENT')

  return (
    <Box>
      <DataSourceSection source={auditTrail.source} />

      <SectionHeading title='Calculation Breakdown' />
      {hasScopeTwo && (
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1.5 }}>
          Scope 2 is reported on two bases and both are retained: <b>location-based</b> uses the regional
          grid-average factor; <b>market-based</b> uses your supplier-specific (or residual-mix) factor.
          Location-based calculations feed your location-based total and market-based calculations feed your
          market-based total. Scope 1 and Scope 3 (including upstream Well-to-Tank) count toward both bases.
          “Preferred method” marks the basis shown in single-value views such as the emissions list.
        </Typography>
      )}
      <Stack spacing={2}>
        {auditTrail.calculations.length === 0 && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            No calculations recorded for this emission.
          </Typography>
        )}
        {contributing.map((calculation) => (
          <CalculationCard key={calculation.id} calculation={calculation} />
        ))}
        {measurements.length > 0 && (
          <>
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              Measurement inputs (recorded for traceability, not counted as emissions in totals)
            </Typography>
            {measurements.map((calculation) => (
              <CalculationCard key={calculation.id} calculation={calculation} />
            ))}
          </>
        )}
      </Stack>

      <ChangeHistorySection auditTrail={auditTrail} />
    </Box>
  )
}
